import { SFDCObjectType } from '../types/enums/SFDCObjectType';
import { FormikErrors } from 'formik';
import { SfdcDisplayType } from '../types/fields';
import { Field } from 'ts-force';

//accessors to the fields table:
export const USED_IN_STAGES = 'usedInStages';
export const USED_IN_AUTOMATIONS = 'usedInAutomations';
export const USED_IN_FUNNELS = 'usedInFunnels';
export const SWEEP_FIELD_NAME = 'sweepFieldName';

//modes for "create/edit" field dialog
export enum EditMode {
  EDIT = 'EDIT',
  CREATE = 'CREATE',
  READONLY = 'READONLY',
}

// Properties that are used in the create/edit field dialog
export type SfThinField = Pick<
  Field,
  | 'picklistValues'
  | 'defaultValueFormula'
  | 'defaultValue'
  | 'scale'
  | 'precision'
  | 'length'
  | 'unique'
  | 'displayLocationInDecimal'
  | 'caseSensitive'
>;

export type NewField = {
  objectName: SFDCObjectType;
  [SWEEP_FIELD_NAME]: string;
  // fieldType: SweepFieldTypes;
  isRequired: boolean;
  description?: string;
  helpText?: string;
  properties: SweepFieldProperties;
  sfDisplayType: SfdcDisplayType;
  sfField: SfThinField;
};

export type FunnelField = Partial<SweepField> & {
  isLeadingField: boolean;
  [USED_IN_STAGES]?: string[];
  [USED_IN_AUTOMATIONS]?: string[];
};

type SfdcDisplayTypeLabels = {
  [fieldType in SfdcDisplayType]?: string;
};

export const NumberLikeFieldTypes = [
  SfdcDisplayType.NUMBER,
  SfdcDisplayType.CURRENCY,
  SfdcDisplayType.PERCENT,
];

export const TextLikeFieldTypes = [
  SfdcDisplayType.STRING,
  SfdcDisplayType.TEXTAREA,
  SfdcDisplayType.LONGTEXTAREA,
];

export const sfdcDisplayTypeLabels: SfdcDisplayTypeLabels = {
  [SfdcDisplayType.BOOLEAN]: 'Checkbox',
  [SfdcDisplayType.CURRENCY]: 'Currency',
  [SfdcDisplayType.DATE]: 'Date',
  [SfdcDisplayType.DATETIME]: 'Date & Time',
  [SfdcDisplayType.EMAIL]: 'Email',
  [SfdcDisplayType.GEOLOCATION]: 'Geolocation',
  [SfdcDisplayType.NUMBER]: 'Number',
  [SfdcDisplayType.PERCENT]: 'Percent',
  [SfdcDisplayType.PHONE]: 'Phone',
  [SfdcDisplayType.PICKLIST]: 'Picklist',
  [SfdcDisplayType.MULTIPICKLIST]: 'Picklist (Multi-select)',
  [SfdcDisplayType.TEXTAREA]: 'Text Area',
  [SfdcDisplayType.LONGTEXTAREA]: 'Long Text Area',
  [SfdcDisplayType.TIME]: 'Time',
  [SfdcDisplayType.URL]: 'URL',
  [SfdcDisplayType.ID]: 'Id',
  [SfdcDisplayType.ADDRESS]: 'Address',
  [SfdcDisplayType.REFERENCE]: 'Lookup',
  [SfdcDisplayType.STRING]: 'Text',
  [SfdcDisplayType.UNKNOWN]: 'Unknown',
};

export interface AttributeProps {
  setFieldValue: (fieldName: string, value: any) => void;
  values: NewField | Partial<FunnelField>;
  errors: FormikErrors<NewField | Partial<FunnelField>>;
  mode: EditMode;
  disabled?: boolean;
  isLeadingField?: boolean;
}

export const textAreaCommonProps = {
  multiline: true,
  autoFocus: false,
  inputProps: {
    maxLength: 255,
  },
};

//SF api names inside "properties"
export const DISPLAY_LOCATION_IN_DECIMAL: keyof SfThinField = 'displayLocationInDecimal';
export const SCALE: keyof SfThinField = 'scale'; //Used by currency, number, percent, geolocation
export const PRECISION: keyof SfThinField = 'precision'; //Used by currency, number, percent
export const LENGTH: keyof SfThinField = 'length'; //UI ONLY PROP for currency, number, percent. Used by BE for text.
export const IS_UNIQUE: keyof SfThinField = 'unique';
export const CASE_SENSITIVE: keyof SfThinField = 'caseSensitive';
export const DEFAULT_VALUE: keyof SfThinField = 'defaultValue';
export const DEFAULT_VALUE_FORMULA: keyof SfThinField = 'defaultValueFormula';

export enum FieldsDialogContentOptions {
  FieldsTable = 'FieldsTable',
  CreateField = 'CreateField',
  EditField = 'EditField',
}

export const FIELD_LABEL_MAX_CHARS = 40;

// SF rules for number-like fields:
export const SCALE_INPUT_PROPS = { min: 0, max: 17, step: 1 };
export const LENGTH_INPUT_PROPS = { min: 1, max: 18, step: 1 };
export const LONGTEXTAREA_INPUT_PROPS = { min: 0, max: 131072 };
export const TEXT_INPUT_PROPS = { min: 0, max: 255 };

export const TEXT_LENGTH_LABEL = 'Length';
export const LENGTH_LABEL = 'Length (number of digits to the left of the decimal point)';
export const SCALE_LABEL = 'Decimal Places (number of digits to the right of the decimal point)';
